import { i18n, type Messages } from "@lingui/core";

import { isSupportedLocale, SOURCE_LOCALE, type SupportedLocale } from "~/config/i18n";
import { messages as sourceMessages } from "~/locales/en/messages";

// Vite resolves all matching files at build time; only the chosen one is fetched
// at runtime. The `en` locale is excluded from the glob because it is already
// statically imported above (shared with I18nProvider) — including it here would
// cause a Vite INEFFECTIVE_DYNAMIC_IMPORT warning and create a redundant chunk.
// The `!` negation prefix excludes the already-bundled source locale.
// Path is relative because the Vite alias (`~/`) doesn't always participate in
// dynamic-import analysis.
const catalogs = import.meta.glob<{ messages: Messages }>([
  "../../locales/*/messages.ts",
  "!../../locales/en/messages.ts",
]);

const catalogPath = (locale: SupportedLocale): string => `../../locales/${locale}/messages.ts`;

export const loadLocale = async (locale: string): Promise<SupportedLocale> => {
  const target: SupportedLocale = isSupportedLocale(locale) ? locale : SOURCE_LOCALE;

  // The source locale catalog is already in the main chunk (static import above).
  // Activating it synchronously avoids an unnecessary network round-trip and
  // prevents the Vite INEFFECTIVE_DYNAMIC_IMPORT warning.
  if (target === SOURCE_LOCALE) {
    i18n.loadAndActivate({ locale: target, messages: sourceMessages });

    return target;
  }

  const loader = catalogs[catalogPath(target)];
  if (!loader) {
    // Catalog isn't compiled yet (run `npx lingui compile`). Activate empty
    // so <Trans message="..."> still renders the source-locale fallback.
    // Warn loudly so a missing compilation step doesn't ship silently to
    // production — surfaces in dev tools and error tracking.
    console.warn(
      `[i18n] No compiled catalog for locale "${target}". ` +
        `Run \`npx lingui compile\` or verify app/locales/${target}/messages.ts exists. ` +
        `Falling back to source-locale text.`,
    );
    i18n.loadAndActivate({ locale: target, messages: {} });

    return target;
  }

  const mod = await loader();

  i18n.loadAndActivate({ locale: target, messages: mod.messages });

  return target;
};
